import { z } from "zod";

import {
  busbarMaterialSchema,
  materialChoiceSchema,
  systemKindSchema,
  temperatureBasisSchema,
  temperatureRiseMethodSchema,
  verificationOutcomeSchema,
  voltageDropBasisSchema,
  type MaterialChoice,
  type SystemKind,
  type TemperatureBasis,
  type TemperatureRiseMethod,
  type VerificationOutcome,
  type VoltageDropBasis,
} from "../services/busbarSelectionContract";
import { jurisdictionProfileSchema } from "../services/cableContract";
import type { ValidationLabels } from "../utils/validationMessages";
import { BUSBAR_MATERIAL_LABELS } from "./busbarEvidenceDraft";
import {
  CASE_LABELS,
  FAULT_LEVEL_SOURCE_LABELS,
  FAULT_LEVEL_SOURCES,
  FAULT_TYPE_LABELS,
  type FaultLevelSource,
} from "./starterCoordinationDraft";

// Draft model of the LV busbar selection form (EOS-07c): what the user is typing, kept apart
// from the components so the payload rules are tested without a browser.
//
// Every value is held as the string the user typed, a choice as its value or "" (Choose...),
// and a yes/no answer as "", "true" or "false" ("" = not answered). The fault level comes
// from one of two places: a stored EOS-04 fault run (LINKED, the default) or a typed value
// (TYPED); the payload carries only the chosen one. A blank optional field is left out, never
// sent as "0" or ""; an optional block whose fields are all blank is left out as a whole.
// Once a block holds any value, its required fields are sent as "" when blank so the schema
// names them by their on-screen label; a choice or answer not given is left out so the schema
// reports it as required.
//
// No engineering rule lives here: the backend engine is the single source of truth. The one
// switch the payload honours is structural: busway data goes with a BUSWAY study only, while
// the rows stay in the draft so choosing BUSWAY again brings them back.

/** The module key the draft is kept under in the browser tab (see useStudyDraft). */
export const BUSBAR_SELECTION_DRAFT_MODULE = "busbar-selection";

// The fault level source, its words and the linked run's case and fault type words are the
// same as the starter coordination and busbar evidence forms', so they are shared.
export { BUSBAR_MATERIAL_LABELS, CASE_LABELS, FAULT_LEVEL_SOURCE_LABELS, FAULT_LEVEL_SOURCES };
export { FAULT_TYPE_LABELS };
export type { FaultLevelSource };

export const BOOLEAN_CHOICES = ["true", "false"] as const;

export type BooleanChoice = "" | (typeof BOOLEAN_CHOICES)[number];

// The three parts of a document reference, as the form holds them. All three verify the
// values of their evidence; the backend decides, this form only carries them (follow-up 21).
export type ReferenceDraft = {
  document: string;
  revisionOrDate: string;
  pageOrClause: string;
};

export type DeratingFactorDraft = {
  id: string;
  name: string;
  value: string;
  source: string;
} & ReferenceDraft;

export type BuswayRowDraft = {
  id: string;
  ratedCurrentA: string;
  material: string;
  icwKa: string;
  icwDurationS: string;
  ipkKa: string;
  voltageDropMvPerAM: string;
  source: string;
  voltageDropBasis: string;
} & ReferenceDraft;

export type BusbarSelectionDraft = {
  projectRevisionId: string;
  runNotes: string;
  code: string;
  name: string;
  busCode: string;
  sldElementCode: string;
  faultLevelSource: FaultLevelSource;
  faultRunId: string;
  typedProspectiveCurrentKa: string;
  typedPeakCurrentKa: string;
  typedSystemNominalVoltageV: string;
  typedFaultBusCode: string;
  requiredFaultDurationS: string;
  systemKind: string;
  material: string;
  maxBarsPerPhase: string;
  designCurrentA: string;
  loadCurrentA: string;
  marginFactor: string;
  arrangementBars: string;
  arrangementWidthMm: string;
  arrangementThicknessMm: string;
  siteAmbientC: string;
  shortCircuitIcwKa: string;
  shortCircuitIcwDurationS: string;
  shortCircuitIpkKa: string;
  shortCircuitDocument: string;
  shortCircuitRevisionOrDate: string;
  shortCircuitPageOrClause: string;
  baseAmpacityA: string;
  baseAmpacitySource: string;
  deratingFactors: DeratingFactorDraft[];
  baseAmpacityAtSiteConditions: BooleanChoice;
  siteConditionsSource: string;
  ampacityDocument: string;
  ampacityRevisionOrDate: string;
  ampacityPageOrClause: string;
  temperatureRiseMethod: string;
  temperatureRiseReferenceDesign: string;
  temperatureRiseVerifiedRatedCurrentA: string;
  temperatureRiseAmbientBasisC: string;
  temperatureBasis: string;
  temperatureValue: string;
  temperaturePermissibleCriterionValue: string;
  temperatureVerificationDocument: string;
  temperatureDocument: string;
  temperatureRevisionOrDate: string;
  temperaturePageOrClause: string;
  supportSpacingMm: string;
  phaseSpacingMm: string;
  supportType: string;
  supportManufacturerReference: string;
  supportDeclaredMaxSpacingMm: string;
  supportDeclaredPeakKa: string;
  supportDocument: string;
  supportRevisionOrDate: string;
  supportPageOrClause: string;
  hasNeutral: BooleanChoice;
  neutralProposedPercentOfPhase: string;
  neutralHarmonicDataAvailable: BooleanChoice;
  neutralCalculatedCurrentA: string;
  neutralRatingA: string;
  neutralDocument: string;
  neutralRevisionOrDate: string;
  neutralPageOrClause: string;
  earthFaultCurrentKa: string;
  earthClearingTimeS: string;
  earthAreaMm2: string;
  earthKFactor: string;
  earthKSource: string;
  earthDocument: string;
  earthRevisionOrDate: string;
  earthPageOrClause: string;
  clearancePhasePhase: string;
  clearancePhaseEarth: string;
  clearanceCreepage: string;
  clearanceSupportShortCircuitStrength: string;
  clearanceDocument: string;
  clearanceRevisionOrDate: string;
  clearancePageOrClause: string;
  buswayRows: BuswayRowDraft[];
  buswayRouteLengthM: string;
  buswayAllowableVoltageDropPercent: string;
  jurisdictionProfile: string;
  notes: string;
};

// A choice the user has not made yet is held as "".
function chosenOrBlank<T extends z.ZodType<string>>(choice: T) {
  return z.union([z.literal(""), choice]);
}

const booleanChoiceSchema = z.union([z.literal(""), z.enum(BOOLEAN_CHOICES)]);

const referenceDraftSchema = {
  document: z.string(),
  revisionOrDate: z.string(),
  pageOrClause: z.string(),
};

const deratingFactorDraftSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    value: z.string(),
    source: z.string(),
    ...referenceDraftSchema,
  })
  .strict();

const buswayRowDraftSchema = z
  .object({
    id: z.string(),
    ratedCurrentA: z.string(),
    material: chosenOrBlank(busbarMaterialSchema),
    icwKa: z.string(),
    icwDurationS: z.string(),
    ipkKa: z.string(),
    voltageDropMvPerAM: z.string(),
    source: z.string(),
    voltageDropBasis: chosenOrBlank(voltageDropBasisSchema),
    ...referenceDraftSchema,
  })
  .strict();

// The shape a draft kept in the browser tab must still have to be restored: exactly the
// fields above, typed text as strings, the choices as values the contract knows or "", and
// no id used by two rows. A draft saved by an older form fails here and is dropped instead
// of half-filling this one.
export const busbarSelectionDraftSchema = z
  .object({
    projectRevisionId: z.string(),
    runNotes: z.string(),
    code: z.string(),
    name: z.string(),
    busCode: z.string(),
    sldElementCode: z.string(),
    faultLevelSource: z.enum(FAULT_LEVEL_SOURCES),
    faultRunId: z.string(),
    typedProspectiveCurrentKa: z.string(),
    typedPeakCurrentKa: z.string(),
    typedSystemNominalVoltageV: z.string(),
    typedFaultBusCode: z.string(),
    requiredFaultDurationS: z.string(),
    systemKind: chosenOrBlank(systemKindSchema),
    material: chosenOrBlank(materialChoiceSchema),
    maxBarsPerPhase: z.string(),
    designCurrentA: z.string(),
    loadCurrentA: z.string(),
    marginFactor: z.string(),
    arrangementBars: z.string(),
    arrangementWidthMm: z.string(),
    arrangementThicknessMm: z.string(),
    siteAmbientC: z.string(),
    shortCircuitIcwKa: z.string(),
    shortCircuitIcwDurationS: z.string(),
    shortCircuitIpkKa: z.string(),
    shortCircuitDocument: z.string(),
    shortCircuitRevisionOrDate: z.string(),
    shortCircuitPageOrClause: z.string(),
    baseAmpacityA: z.string(),
    baseAmpacitySource: z.string(),
    deratingFactors: z.array(deratingFactorDraftSchema),
    baseAmpacityAtSiteConditions: booleanChoiceSchema,
    siteConditionsSource: z.string(),
    ampacityDocument: z.string(),
    ampacityRevisionOrDate: z.string(),
    ampacityPageOrClause: z.string(),
    temperatureRiseMethod: chosenOrBlank(temperatureRiseMethodSchema),
    temperatureRiseReferenceDesign: z.string(),
    temperatureRiseVerifiedRatedCurrentA: z.string(),
    temperatureRiseAmbientBasisC: z.string(),
    temperatureBasis: chosenOrBlank(temperatureBasisSchema),
    temperatureValue: z.string(),
    temperaturePermissibleCriterionValue: z.string(),
    temperatureVerificationDocument: z.string(),
    temperatureDocument: z.string(),
    temperatureRevisionOrDate: z.string(),
    temperaturePageOrClause: z.string(),
    supportSpacingMm: z.string(),
    phaseSpacingMm: z.string(),
    supportType: z.string(),
    supportManufacturerReference: z.string(),
    supportDeclaredMaxSpacingMm: z.string(),
    supportDeclaredPeakKa: z.string(),
    supportDocument: z.string(),
    supportRevisionOrDate: z.string(),
    supportPageOrClause: z.string(),
    hasNeutral: booleanChoiceSchema,
    neutralProposedPercentOfPhase: z.string(),
    neutralHarmonicDataAvailable: booleanChoiceSchema,
    neutralCalculatedCurrentA: z.string(),
    neutralRatingA: z.string(),
    neutralDocument: z.string(),
    neutralRevisionOrDate: z.string(),
    neutralPageOrClause: z.string(),
    earthFaultCurrentKa: z.string(),
    earthClearingTimeS: z.string(),
    earthAreaMm2: z.string(),
    earthKFactor: z.string(),
    earthKSource: z.string(),
    earthDocument: z.string(),
    earthRevisionOrDate: z.string(),
    earthPageOrClause: z.string(),
    clearancePhasePhase: chosenOrBlank(verificationOutcomeSchema),
    clearancePhaseEarth: chosenOrBlank(verificationOutcomeSchema),
    clearanceCreepage: chosenOrBlank(verificationOutcomeSchema),
    clearanceSupportShortCircuitStrength: chosenOrBlank(verificationOutcomeSchema),
    clearanceDocument: z.string(),
    clearanceRevisionOrDate: z.string(),
    clearancePageOrClause: z.string(),
    buswayRows: z.array(buswayRowDraftSchema),
    buswayRouteLengthM: z.string(),
    buswayAllowableVoltageDropPercent: z.string(),
    jurisdictionProfile: jurisdictionProfileSchema,
    notes: z.string(),
  })
  .strict()
  .superRefine((draft, context) => {
    const ids = [...draft.deratingFactors, ...draft.buswayRows].map((row) => row.id);
    if (new Set(ids).size !== ids.length) {
      context.addIssue({ code: "custom", message: "Two rows use the same id." });
    }
  });

let nextRowNumber = 1;

export function createRowId(prefix: "derating" | "busway"): string {
  const id = `${prefix}-${nextRowNumber}`;
  nextRowNumber += 1;
  return id;
}

// Derating factors and busway rows share one counter that starts again at 1 on every page
// load, while a restored draft brings the ids it was saved with. Moving the counter past every
// id in use keeps a row added afterwards from taking the id of one already on screen.
export function seedRowIdCounter(ids: readonly string[]): void {
  for (const id of ids) {
    const match = /^(?:derating|busway)-(\d+)$/.exec(id);
    if (match !== null) {
      nextRowNumber = Math.max(nextRowNumber, Number(match[1]) + 1);
    }
  }
}

const BLANK_REFERENCE: ReferenceDraft = { document: "", revisionOrDate: "", pageOrClause: "" };

export function createDeratingFactorDraft(): DeratingFactorDraft {
  return { id: createRowId("derating"), name: "", value: "", source: "", ...BLANK_REFERENCE };
}

export function createBuswayRowDraft(): BuswayRowDraft {
  return {
    id: createRowId("busway"),
    ratedCurrentA: "",
    material: "",
    icwKa: "",
    icwDurationS: "",
    ipkKa: "",
    voltageDropMvPerAM: "",
    source: "",
    voltageDropBasis: "",
    ...BLANK_REFERENCE,
  };
}

// Only the defaults the backend itself has are filled in: a panel busbar and the IN profile.
// The fault level starts on the linked run with no run chosen yet; every other field waits
// for the engineer's figure and every other choice starts blank.
export function initialBusbarSelectionDraft(): BusbarSelectionDraft {
  return {
    projectRevisionId: "",
    runNotes: "",
    code: "",
    name: "",
    busCode: "",
    sldElementCode: "",
    faultLevelSource: "LINKED",
    faultRunId: "",
    typedProspectiveCurrentKa: "",
    typedPeakCurrentKa: "",
    typedSystemNominalVoltageV: "",
    typedFaultBusCode: "",
    requiredFaultDurationS: "",
    systemKind: "PANEL_BUSBAR",
    material: "",
    maxBarsPerPhase: "",
    designCurrentA: "",
    loadCurrentA: "",
    marginFactor: "",
    arrangementBars: "",
    arrangementWidthMm: "",
    arrangementThicknessMm: "",
    siteAmbientC: "",
    shortCircuitIcwKa: "",
    shortCircuitIcwDurationS: "",
    shortCircuitIpkKa: "",
    shortCircuitDocument: "",
    shortCircuitRevisionOrDate: "",
    shortCircuitPageOrClause: "",
    baseAmpacityA: "",
    baseAmpacitySource: "",
    deratingFactors: [],
    baseAmpacityAtSiteConditions: "",
    siteConditionsSource: "",
    ampacityDocument: "",
    ampacityRevisionOrDate: "",
    ampacityPageOrClause: "",
    temperatureRiseMethod: "",
    temperatureRiseReferenceDesign: "",
    temperatureRiseVerifiedRatedCurrentA: "",
    temperatureRiseAmbientBasisC: "",
    temperatureBasis: "",
    temperatureValue: "",
    temperaturePermissibleCriterionValue: "",
    temperatureVerificationDocument: "",
    temperatureDocument: "",
    temperatureRevisionOrDate: "",
    temperaturePageOrClause: "",
    supportSpacingMm: "",
    phaseSpacingMm: "",
    supportType: "",
    supportManufacturerReference: "",
    supportDeclaredMaxSpacingMm: "",
    supportDeclaredPeakKa: "",
    supportDocument: "",
    supportRevisionOrDate: "",
    supportPageOrClause: "",
    hasNeutral: "",
    neutralProposedPercentOfPhase: "",
    neutralHarmonicDataAvailable: "",
    neutralCalculatedCurrentA: "",
    neutralRatingA: "",
    neutralDocument: "",
    neutralRevisionOrDate: "",
    neutralPageOrClause: "",
    earthFaultCurrentKa: "",
    earthClearingTimeS: "",
    earthAreaMm2: "",
    earthKFactor: "",
    earthKSource: "",
    earthDocument: "",
    earthRevisionOrDate: "",
    earthPageOrClause: "",
    clearancePhasePhase: "",
    clearancePhaseEarth: "",
    clearanceCreepage: "",
    clearanceSupportShortCircuitStrength: "",
    clearanceDocument: "",
    clearanceRevisionOrDate: "",
    clearancePageOrClause: "",
    buswayRows: [],
    buswayRouteLengthM: "",
    buswayAllowableVoltageDropPercent: "",
    jurisdictionProfile: "IN",
    notes: "",
  };
}

type FieldKind = "text" | "integer" | "choice" | "boolean";

type FieldSpec = {
  key: string;
  value: string;
  kind?: FieldKind;
  required?: boolean;
};

// One field as the payload carries it, or undefined when it is left out. A blank required
// text or integer is sent as "" so the schema names it; a blank optional field, choice or
// answer is left out. An integer typed as digits is sent as a number, anything else as typed
// so the schema reports it.
function fieldValue(spec: FieldSpec): unknown {
  const value = spec.value.trim();
  const kind = spec.kind ?? "text";
  if (value === "") {
    return spec.required === true && (kind === "text" || kind === "integer") ? "" : undefined;
  }
  if (kind === "boolean") {
    return value === "true" ? true : value === "false" ? false : value;
  }
  if (kind === "integer" && /^-?\d+$/.test(value)) {
    return Number(value);
  }
  return value;
}

function fields(specs: readonly FieldSpec[]): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const spec of specs) {
    const value = fieldValue(spec);
    if (value !== undefined) {
      result[spec.key] = value;
    }
  }
  return result;
}

function isBlank(specs: readonly FieldSpec[]): boolean {
  return specs.every((spec) => spec.value.trim() === "");
}

// An optional block: left out when every field is blank.
function block(specs: readonly FieldSpec[]): Record<string, unknown> | undefined {
  return isBlank(specs) ? undefined : fields(specs);
}

// The document reference of one evidence record or row: left out when all three are blank.
// Nothing is judged here - the backend decides what is verified (follow-up 21).
function referencePayload(reference: ReferenceDraft): Record<string, unknown> | undefined {
  return block([
    { key: "document", value: reference.document },
    { key: "revision_or_date", value: reference.revisionOrDate },
    { key: "page_or_clause", value: reference.pageOrClause },
  ]);
}

// An optional block that carries a document reference: left out when the block's own fields
// and the reference are all blank.
function referencedBlock(
  specs: readonly FieldSpec[],
  reference: ReferenceDraft,
): Record<string, unknown> | undefined {
  const nested = referencePayload(reference);
  if (isBlank(specs) && nested === undefined) {
    return undefined;
  }
  return { ...fields(specs), ...withBlock("reference", nested) };
}

// A list of rows: a row left entirely blank (its reference too) is dropped, a half-filled one
// is sent.
function rows<T>(
  list: readonly T[],
  toPayload: (row: T) => Record<string, unknown> | undefined,
): Record<string, unknown>[] {
  return list
    .map((row) => toPayload(row))
    .filter((row): row is Record<string, unknown> => row !== undefined);
}

// The reference fields of one block, which the flat draft names <prefix>Document,
// <prefix>RevisionOrDate and <prefix>PageOrClause.
type ReferencePrefix = "shortCircuit" | "ampacity" | "temperature" | "support" | "neutral" | "earth" | "clearance";

function referenceOf(draft: BusbarSelectionDraft, prefix: ReferencePrefix): ReferenceDraft {
  return {
    document: draft[`${prefix}Document`],
    revisionOrDate: draft[`${prefix}RevisionOrDate`],
    pageOrClause: draft[`${prefix}PageOrClause`],
  };
}

function faultLevelPayload(draft: BusbarSelectionDraft): Record<string, unknown> {
  if (draft.faultLevelSource === "TYPED") {
    return {
      typed: fields([
        { key: "prospective_current_ka", value: draft.typedProspectiveCurrentKa, required: true },
        { key: "peak_current_ka", value: draft.typedPeakCurrentKa },
        { key: "system_nominal_voltage_v", value: draft.typedSystemNominalVoltageV, required: true },
        { key: "fault_bus_code", value: draft.typedFaultBusCode, required: true },
      ]),
    };
  }
  return { fault_run_id: draft.faultRunId.trim() };
}

function deratingFactorPayload(row: DeratingFactorDraft): Record<string, unknown> | undefined {
  return referencedBlock(
    [
      { key: "name", value: row.name, required: true },
      { key: "value", value: row.value, required: true },
      { key: "source", value: row.source },
    ],
    row,
  );
}

function ampacityPayload(draft: BusbarSelectionDraft): Record<string, unknown> | undefined {
  const scalars: FieldSpec[] = [
    { key: "base_ampacity_a", value: draft.baseAmpacityA },
    { key: "base_ampacity_source", value: draft.baseAmpacitySource },
    {
      key: "base_ampacity_at_site_conditions",
      value: draft.baseAmpacityAtSiteConditions,
      kind: "boolean",
    },
    { key: "site_conditions_source", value: draft.siteConditionsSource },
  ];
  const factors = rows(draft.deratingFactors, deratingFactorPayload);
  const reference = referencePayload(referenceOf(draft, "ampacity"));
  if (isBlank(scalars) && factors.length === 0 && reference === undefined) {
    return undefined;
  }
  return {
    ...fields(scalars),
    ...(factors.length === 0 ? {} : { derating_factors: factors }),
    ...withBlock("reference", reference),
  };
}

function buswayRowPayload(row: BuswayRowDraft): Record<string, unknown> | undefined {
  return referencedBlock(
    [
      { key: "rated_current_a", value: row.ratedCurrentA, required: true },
      { key: "material", value: row.material, kind: "choice" },
      { key: "icw_ka", value: row.icwKa, required: true },
      { key: "icw_duration_s", value: row.icwDurationS, required: true },
      { key: "ipk_ka", value: row.ipkKa, required: true },
      { key: "voltage_drop_mv_per_a_m", value: row.voltageDropMvPerAM, required: true },
      { key: "source", value: row.source },
      { key: "voltage_drop_basis", value: row.voltageDropBasis, kind: "choice" },
    ],
    row,
  );
}

// Busway data goes with a BUSWAY study only; the draft keeps its rows whatever the kind.
function buswayPayload(draft: BusbarSelectionDraft): Record<string, unknown> | undefined {
  if (draft.systemKind !== "BUSWAY") {
    return undefined;
  }
  const scalars: FieldSpec[] = [
    { key: "route_length_m", value: draft.buswayRouteLengthM, required: true },
    {
      key: "allowable_voltage_drop_percent",
      value: draft.buswayAllowableVoltageDropPercent,
      required: true,
    },
  ];
  const listed = rows(draft.buswayRows, buswayRowPayload);
  if (isBlank(scalars) && listed.length === 0) {
    return undefined;
  }
  return { ...(listed.length === 0 ? {} : { rows: listed }), ...fields(scalars) };
}

function withBlock(key: string, value: Record<string, unknown> | undefined) {
  return value === undefined ? {} : { [key]: value };
}

// The request body as the form will send it. It is validated afterwards with
// busbarSelectionRunCreateRequestSchema, so this function does not judge the values.
export function buildBusbarSelectionPayload(draft: BusbarSelectionDraft): Record<string, unknown> {
  const study: Record<string, unknown> = {
    ...fields([
      { key: "code", value: draft.code, required: true },
      { key: "name", value: draft.name, required: true },
      { key: "bus_code", value: draft.busCode, required: true },
      { key: "sld_element_code", value: draft.sldElementCode },
    ]),
    ...faultLevelPayload(draft),
    ...fields([
      { key: "required_fault_duration_s", value: draft.requiredFaultDurationS, required: true },
      { key: "system_kind", value: draft.systemKind, kind: "choice" },
      { key: "material", value: draft.material, kind: "choice" },
      { key: "max_bars_per_phase", value: draft.maxBarsPerPhase, kind: "integer" },
      { key: "design_current_a", value: draft.designCurrentA },
      { key: "load_current_a", value: draft.loadCurrentA },
      { key: "margin_factor", value: draft.marginFactor },
    ]),
    ...withBlock(
      "selected_arrangement",
      block([
        { key: "bars", value: draft.arrangementBars, kind: "integer", required: true },
        { key: "width_mm", value: draft.arrangementWidthMm, required: true },
        { key: "thickness_mm", value: draft.arrangementThicknessMm, required: true },
      ]),
    ),
    ...fields([{ key: "site_ambient_c", value: draft.siteAmbientC }]),
    ...withBlock(
      "short_circuit",
      referencedBlock([
        { key: "icw_ka", value: draft.shortCircuitIcwKa },
        { key: "icw_duration_s", value: draft.shortCircuitIcwDurationS },
        { key: "ipk_ka", value: draft.shortCircuitIpkKa },
      ], referenceOf(draft, "shortCircuit")),
    ),
    ...withBlock("ampacity", ampacityPayload(draft)),
    ...withBlock(
      "temperature_rise",
      referencedBlock([
        { key: "method", value: draft.temperatureRiseMethod, kind: "choice" },
        { key: "reference_design", value: draft.temperatureRiseReferenceDesign },
        { key: "verified_rated_current_a", value: draft.temperatureRiseVerifiedRatedCurrentA },
        { key: "ambient_basis_c", value: draft.temperatureRiseAmbientBasisC },
        { key: "temperature_basis", value: draft.temperatureBasis, kind: "choice" },
        { key: "temperature_value", value: draft.temperatureValue },
        {
          key: "permissible_criterion_value",
          value: draft.temperaturePermissibleCriterionValue,
        },
        { key: "verification_document", value: draft.temperatureVerificationDocument },
      ], referenceOf(draft, "temperature")),
    ),
    ...withBlock(
      "supports",
      referencedBlock([
        { key: "support_spacing_mm", value: draft.supportSpacingMm },
        { key: "phase_spacing_mm", value: draft.phaseSpacingMm },
        { key: "support_type", value: draft.supportType },
        { key: "manufacturer_reference", value: draft.supportManufacturerReference },
        { key: "declared_max_support_spacing_mm", value: draft.supportDeclaredMaxSpacingMm },
        { key: "declared_peak_ka", value: draft.supportDeclaredPeakKa },
      ], referenceOf(draft, "support")),
    ),
    ...withBlock(
      "neutral",
      referencedBlock([
        { key: "has_neutral", value: draft.hasNeutral, kind: "boolean" },
        { key: "proposed_percent_of_phase", value: draft.neutralProposedPercentOfPhase },
        {
          key: "harmonic_data_available",
          value: draft.neutralHarmonicDataAvailable,
          kind: "boolean",
        },
        { key: "calculated_neutral_current_a", value: draft.neutralCalculatedCurrentA },
        { key: "neutral_rating_a", value: draft.neutralRatingA },
      ], referenceOf(draft, "neutral")),
    ),
    ...withBlock(
      "protective_earth",
      referencedBlock([
        { key: "earth_fault_current_ka", value: draft.earthFaultCurrentKa, required: true },
        { key: "clearing_time_s", value: draft.earthClearingTimeS, required: true },
        { key: "pe_area_mm2", value: draft.earthAreaMm2, required: true },
        { key: "k_factor", value: draft.earthKFactor },
        { key: "k_source", value: draft.earthKSource },
      ], referenceOf(draft, "earth")),
    ),
    ...withBlock(
      "clearance",
      referencedBlock([
        { key: "phase_phase", value: draft.clearancePhasePhase, kind: "choice" },
        { key: "phase_earth", value: draft.clearancePhaseEarth, kind: "choice" },
        { key: "creepage", value: draft.clearanceCreepage, kind: "choice" },
        {
          key: "support_short_circuit_strength",
          value: draft.clearanceSupportShortCircuitStrength,
          kind: "choice",
        },
      ], referenceOf(draft, "clearance")),
    ),
    ...withBlock("busway", buswayPayload(draft)),
    ...fields([
      { key: "jurisdiction_profile", value: draft.jurisdictionProfile, kind: "choice" },
      { key: "notes", value: draft.notes },
    ]),
  };
  return {
    study,
    ...fields([
      { key: "project_revision_id", value: draft.projectRevisionId },
      { key: "notes", value: draft.runNotes },
    ]),
  };
}

// On-screen labels of the busbar selection form, keyed by the request path with list indexes
// replaced by "*". The run request nests the study under "study", so every study path carries
// that prefix.
export const BUSBAR_SELECTION_LABELS: ValidationLabels = {
  fields: {
    project_revision_id: "Project revision",
    notes: "Run note",
    "study.code": "Study code",
    "study.name": "Study name",
    "study.bus_code": "Bus code",
    "study.sld_element_code": "Single-line diagram bus",
    "study.fault_run_id": "Fault run",
    "study.typed": "Fault level source",
    "study.typed.prospective_current_ka": "Prospective current Ik'' (kA)",
    "study.typed.peak_current_ka": "Peak current ip (kA)",
    "study.typed.system_nominal_voltage_v": "System nominal voltage (V)",
    "study.typed.fault_bus_code": "Fault bus code",
    "study.required_fault_duration_s": "Required fault duration (s)",
    "study.system_kind": "System kind",
    "study.material": "Conductor material",
    "study.max_bars_per_phase": "Maximum bars per phase",
    "study.design_current_a": "Design current (A)",
    "study.load_current_a": "Load current (A)",
    "study.margin_factor": "Margin factor",
    "study.selected_arrangement": "Selected arrangement",
    "study.selected_arrangement.bars": "Bars per phase",
    "study.selected_arrangement.width_mm": "Bar width (mm)",
    "study.selected_arrangement.thickness_mm": "Bar thickness (mm)",
    "study.site_ambient_c": "Site ambient (°C)",
    "study.short_circuit": "Short-circuit ratings",
    "study.short_circuit.icw_ka": "Rated short-time withstand current Icw (kA)",
    "study.short_circuit.icw_duration_s": "Icw duration (s)",
    "study.short_circuit.ipk_ka": "Rated peak withstand current Ipk (kA)",
    "study.short_circuit.reference": "Reference",
    "study.short_circuit.reference.document": "Document",
    "study.short_circuit.reference.revision_or_date": "Revision / date",
    "study.short_circuit.reference.page_or_clause": "Page / clause",
    "study.ampacity": "Ampacity evidence",
    "study.ampacity.base_ampacity_a": "Base ampacity (A)",
    "study.ampacity.base_ampacity_source": "Base ampacity source",
    "study.ampacity.derating_factors": "Derating factors",
    "study.ampacity.derating_factors.*.name": "Name",
    "study.ampacity.derating_factors.*.value": "Factor",
    "study.ampacity.derating_factors.*.source": "Source",
    "study.ampacity.derating_factors.*.reference": "Reference",
    "study.ampacity.derating_factors.*.reference.document": "Document",
    "study.ampacity.derating_factors.*.reference.revision_or_date": "Revision / date",
    "study.ampacity.derating_factors.*.reference.page_or_clause": "Page / clause",
    "study.ampacity.base_ampacity_at_site_conditions": "Base ampacity is at site conditions",
    "study.ampacity.site_conditions_source": "Site conditions source",
    "study.ampacity.reference": "Reference",
    "study.ampacity.reference.document": "Document",
    "study.ampacity.reference.revision_or_date": "Revision / date",
    "study.ampacity.reference.page_or_clause": "Page / clause",
    "study.temperature_rise": "Temperature rise evidence",
    "study.temperature_rise.method": "Verification method",
    "study.temperature_rise.reference_design": "Reference design",
    "study.temperature_rise.verified_rated_current_a": "Verified rated current (A)",
    "study.temperature_rise.ambient_basis_c": "Ambient of the verification (°C)",
    "study.temperature_rise.temperature_basis": "Temperature figure is a",
    "study.temperature_rise.temperature_value": "Temperature figure (K or °C)",
    "study.temperature_rise.permissible_criterion_value": "Permissible criterion (K or °C)",
    "study.temperature_rise.verification_document": "Verification document",
    "study.temperature_rise.reference": "Reference",
    "study.temperature_rise.reference.document": "Document",
    "study.temperature_rise.reference.revision_or_date": "Revision / date",
    "study.temperature_rise.reference.page_or_clause": "Page / clause",
    "study.supports": "Supports",
    "study.supports.support_spacing_mm": "Support spacing (mm)",
    "study.supports.phase_spacing_mm": "Phase spacing (mm)",
    "study.supports.support_type": "Support type",
    "study.supports.manufacturer_reference": "Manufacturer reference",
    "study.supports.declared_max_support_spacing_mm": "Declared maximum support spacing (mm)",
    "study.supports.declared_peak_ka": "Declared peak withstand (kA)",
    "study.supports.reference": "Reference",
    "study.supports.reference.document": "Document",
    "study.supports.reference.revision_or_date": "Revision / date",
    "study.supports.reference.page_or_clause": "Page / clause",
    "study.neutral": "Neutral bar",
    "study.neutral.has_neutral": "Neutral bar fitted",
    "study.neutral.proposed_percent_of_phase": "Neutral size (% of phase)",
    "study.neutral.harmonic_data_available": "Harmonic data available",
    "study.neutral.calculated_neutral_current_a": "Calculated neutral current (A)",
    "study.neutral.neutral_rating_a": "Neutral bar rated current (A)",
    "study.neutral.reference": "Reference",
    "study.neutral.reference.document": "Document",
    "study.neutral.reference.revision_or_date": "Revision / date",
    "study.neutral.reference.page_or_clause": "Page / clause",
    "study.protective_earth": "Protective earth bar",
    "study.protective_earth.earth_fault_current_ka": "Earth fault current (kA)",
    "study.protective_earth.clearing_time_s": "Clearing time (s)",
    "study.protective_earth.pe_area_mm2": "PE bar area (mm²)",
    "study.protective_earth.k_factor": "k factor",
    "study.protective_earth.k_source": "k factor source",
    "study.protective_earth.reference": "Reference",
    "study.protective_earth.reference.document": "Document",
    "study.protective_earth.reference.revision_or_date": "Revision / date",
    "study.protective_earth.reference.page_or_clause": "Page / clause",
    "study.clearance": "Clearances",
    "study.clearance.phase_phase": "Phase-to-phase clearance",
    "study.clearance.phase_earth": "Phase-to-earth clearance",
    "study.clearance.creepage": "Creepage",
    "study.clearance.support_short_circuit_strength": "Support short-circuit strength",
    "study.clearance.reference": "Reference",
    "study.clearance.reference.document": "Document",
    "study.clearance.reference.revision_or_date": "Revision / date",
    "study.clearance.reference.page_or_clause": "Page / clause",
    "study.busway": "Busway",
    "study.busway.rows": "Busway rows",
    "study.busway.rows.*.rated_current_a": "Rated current (A)",
    "study.busway.rows.*.material": "Conductor material",
    "study.busway.rows.*.icw_ka": "Icw (kA)",
    "study.busway.rows.*.icw_duration_s": "Icw duration (s)",
    "study.busway.rows.*.ipk_ka": "Ipk (kA)",
    "study.busway.rows.*.voltage_drop_mv_per_a_m": "Voltage drop (mV/A/m)",
    "study.busway.rows.*.source": "Source",
    "study.busway.rows.*.voltage_drop_basis": "Voltage drop basis",
    "study.busway.rows.*.reference": "Reference",
    "study.busway.rows.*.reference.document": "Document",
    "study.busway.rows.*.reference.revision_or_date": "Revision / date",
    "study.busway.rows.*.reference.page_or_clause": "Page / clause",
    "study.busway.route_length_m": "Route length (m)",
    "study.busway.allowable_voltage_drop_percent": "Allowable voltage drop (%)",
    "study.jurisdiction_profile": "Jurisdiction profile",
    "study.notes": "Study notes",
  },
  groups: {
    derating_factors: "Derating factor",
    rows: "Busway row",
  },
};

// The words of every choice the form offers, typed against the contract enums so a value the
// contract gains cannot pass without a label.

export const SYSTEM_KIND_LABELS: Record<SystemKind, string> = {
  PANEL_BUSBAR: "Panel busbar",
  BUSWAY: "Busway (compact busbar trunking)",
};

export const MATERIAL_CHOICE_LABELS: Record<MaterialChoice, string> = {
  COPPER: "Copper",
  ALUMINIUM: "Aluminium",
  BOTH: "Both (compare copper and aluminium)",
};

export const VERIFICATION_OUTCOME_LABELS: Record<VerificationOutcome, string> = {
  VERIFIED: "Verified",
  FAILED: "Failed",
};

export const TEMPERATURE_BASIS_LABELS: Record<TemperatureBasis, string> = {
  RISE: "Temperature rise",
  ABSOLUTE: "Absolute temperature",
};

export const TEMPERATURE_RISE_METHOD_LABELS: Record<TemperatureRiseMethod, string> = {
  TEST: "Type test",
  COMPARISON: "Comparison with a tested design",
  CALCULATION: "Calculation",
  MANUFACTURER_VERIFIED_DESIGN: "Manufacturer verified design",
};

export const VOLTAGE_DROP_BASIS_LABELS: Record<VoltageDropBasis, string> = {
  LINE_TO_LINE: "Line to line",
  LINE_TO_NEUTRAL: "Line to neutral",
};
